import type {
    CategoryResult,
    CategoryVariables
} from '@pylot-data/api/operations/get-category'
import type { SearchCriteria } from '@corratech/pylot-filters-and-sort'
import { SearchCriteriaFilterActions } from '@corratech/pylot-filters-and-sort'
import {
    SwrUsePaginatedQueryOutput,
    usePaginatedQuery
} from '@pylot-data/hooks/use-paginated-query'
import type { UsePaginatedQuerySwrOptions } from '@pylot-data/hooks/use-paginated-query'
import { SwrUseQueryOutput, useQuery } from '@pylot-data/hooks/use-query'
import type { UseQuerySwrOptions } from '@pylot-data/hooks/use-query'
import type {
    CategoryTree,
    ProductInterface,
    Maybe
} from '@pylot-data/pylotschema'
import type { Products, QueryProductsArgs } from '@pylot-data/fwrdschema'
import type { SWRConfiguration } from 'swr'
import type { SWRInfiniteConfiguration } from 'swr/infinite'
import {
    oldCategoryQuery,
    productsQuery
} from '@pylot-data/hooks/category/graphql/categoryQuery'
import type { RequestInit } from '@vercel/fetch'
import { GraphQLError } from '@pylot-data/graphqlFetch'
import { SortEnum } from '@pylot-data/enums/SortEnum'

type UseCategoryResult = {
    category?: Maybe<CategoryTree>
    productsResult?: Maybe<Products>
    productItems: ProductInterface[]
    currentPageNumber: number
    isLast: boolean
    isLoading: boolean
    /** Una página volvió con errores GraphQL y sin productos: se ofrece reintentar. */
    hasPageError: boolean
    error: GraphQLError | undefined
    categoriesSwrResult: SwrUseQueryOutput<CategoryResult>
    productsSwrResult: SwrUsePaginatedQueryOutput<{
        products: Products
    }>
}

/**
 * Custom hook to fetch category and paginated product data.
 *
 * @param urlPath - Category URL path
 * @param categoryUid - Category UID
 * @param productsInput - Product query args (with optional searchCriteria)
 * @param categorySwrOptions - SWR config for category
 * @param productSwrOptions - SWR config for products (infinite)
 * @param fetchOptions - Fetch options
 * @param disabled - If true, disables fetching
 * @param subsequentRequestsPageSize - Optional page size for subsequent requests
 */
export const useCategory = (
    urlPath: string,
    categoryUid: string,
    productsInput: QueryProductsArgs & { searchCriteria?: SearchCriteria[] },
    categorySwrOptions: UseQuerySwrOptions,
    productSwrOptions: UsePaginatedQuerySwrOptions = {},
    fetchOptions: RequestInit,
    disabled = false,
    subsequentRequestsPageSize?: number
): UseCategoryResult => {
    const { pageSize, searchCriteria = [], sort } = productsInput

    // Fetch category data
    const categoriesSwrResult = useQuery<CategoryVariables, CategoryResult>(
        disabled ? null : oldCategoryQuery,
        {
            categoryFilter: {
                url_path: {
                    eq: urlPath
                }
            }
        },
        categorySwrOptions,
        fetchOptions
    )

    // Resiliencia: si la página se sirvió como shell (SSR falló y categoryUid
    // llega vacío en props), usar el uid de la categoría ya fetcheada por el
    // cliente para no dejar la query de productos deshabilitada para siempre.
    const fetchedCategoryUid =
        categoriesSwrResult.data?.data?.categories?.items?.[0]?.uid ?? ''
    const effectiveCategoryUid = categoryUid || fetchedCategoryUid

    // Use .some instead of .find for boolean intent, clarify variable name
    const hasCategoryFilter = searchCriteria.some(
        (item: { attribute_code: string }) =>
            ['category_id', 'category_uid'].includes(item.attribute_code)
    )
    // Prepare search criteria, always ensure category_uid is present
    const processedSearchCriteria = hasCategoryFilter
        ? [...searchCriteria]
        : [
              ...searchCriteria,
              {
                  attribute_code: 'category_uid',
                  filter_action: SearchCriteriaFilterActions.EQ,
                  filter_value: effectiveCategoryUid
              }
          ]

    // Fetch paginated products data
    const productsSwrResult = usePaginatedQuery<
        QueryProductsArgs & { searchCriteria?: SearchCriteria[] },
        { products: Products }
    >(
        !disabled && effectiveCategoryUid ? productsQuery : null,
        {
            // `currentPage` will be automatically replaced with usePaginatedQuery's `size` prop
            currentPage: 1,
            pageSize,
            sort: sort?.price_desc ? { price_desc: SortEnum.Desc } : sort,
            searchCriteria: processedSearchCriteria
        },
        'currentPage',
        productSwrOptions,
        fetchOptions,
        subsequentRequestsPageSize
    )

    // Calculate current page index
    const { size } = productsSwrResult
    const initialSizeOffset = (productSwrOptions.initialSize ?? 1) - 1
    const currentPageIndex = size - 1 - initialSizeOffset

    // Extract category and products data
    const category = categoriesSwrResult.data?.data?.categories?.items?.[0]
    const loadedPages = productsSwrResult.data ?? []
    // Si la página visible volvió sin `products` (error GraphQL) o todavía no está, se usa la
    // última que respondió: mantiene total, filtros y orden, y evita vaciar la grilla.
    let productsResult =
        productsSwrResult.data?.[currentPageIndex]?.data?.products
    for (
        let index = Math.min(currentPageIndex, loadedPages.length - 1);
        !productsResult && index >= 0;
        index--
    ) {
        productsResult = loadedPages[index]?.data?.products
    }
    const pageInfo = productsResult?.page_info
    const currentPage = pageInfo?.current_page
    const totalPages = pageInfo?.total_pages

    // La última página pedida (la visible o la prefetch) volvió con errores y sin `products`;
    // usePaginatedQuery no pide más detrás de ella.
    const lastLoadedPage = loadedPages[loadedPages.length - 1]
    const hasFailedPage =
        !!lastLoadedPage?.errors?.length && !lastLoadedPage?.data?.products
    // "currentPage mayor que total_pages": el catálogo se achicó con la pestaña abierta (p. ej. al
    // empezar un evento). No quedan páginas, así que la lista termina en vez de ofrecer reintento.
    const isPageOutOfRange =
        hasFailedPage &&
        !!lastLoadedPage?.errors?.some((pageError) =>
            /currentPage/i.test(pageError?.message ?? '')
        )

    // Determine if this is the last page
    const isLast =
        (currentPage !== undefined && totalPages !== undefined
            ? currentPage === totalPages
            : false) || isPageOutOfRange

    let isLoadingData = productsSwrResult.isValidating

    let productItems: ProductInterface[] = []

    if (
        productsSwrResult?.data?.length &&
        typeof productsResult !== 'undefined'
    ) {
        const pages = productsSwrResult.data

        // CHANGE: Clarify isLoadingData logic for pre-loading
        isLoadingData =
            pages.length > 0 && isLast && !productsSwrResult.isValidating
                ? false
                : currentPageIndex >= pages.length - 1

        // Aggregate product items up to the current page
        for (
            let index = 0;
            index <= currentPageIndex && index < pages.length;
            index++
        ) {
            const pageItems = pages[index].data?.products?.items
            productItems = [
                ...productItems,
                ...(Array.isArray(pageItems)
                    ? (pageItems as ProductInterface[])
                    : [])
            ]
        }
    }

    // Si la página prefetch falló (p. ej. 504 del gateway) no hay que quedarse
    // "cargando" para siempre: eso bloqueaba useLoadMoreButton y el scroll
    // infinito nunca volvía a pedir productos hasta recargar la página.
    if (
        (productsSwrResult.error || hasFailedPage) &&
        !productsSwrResult.isValidating
    ) {
        isLoadingData = false
    }

    return {
        category,
        productsResult,
        productItems,
        isLast,
        isLoading: isLoadingData,
        hasPageError: hasFailedPage && !isPageOutOfRange,
        currentPageNumber: currentPageIndex + 1,
        error: categoriesSwrResult.error || productsSwrResult.error,
        categoriesSwrResult,
        productsSwrResult
    }
}
